import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Plus, Edit2, Trash2, Search, Building2, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SALARY_FIELDS = [
  { key: 'salario_junior_i', label: 'Júnior I' },
  { key: 'salario_junior_ii', label: 'Júnior II' },
  { key: 'salario_junior_iii', label: 'Júnior III' },
  { key: 'salario_pleno_i', label: 'Pleno I' },
  { key: 'salario_pleno_ii', label: 'Pleno II' },
  { key: 'salario_pleno_iii', label: 'Pleno III' },
  { key: 'salario_senior_i', label: 'Sênior I' },
  { key: 'salario_senior_ii', label: 'Sênior II' },
  { key: 'salario_senior_iii', label: 'Sênior III' },
];

const TAB_CONFIG = {
  cadastros_empresas: {
    endpoint: '/companies',
    label: 'Empresa',
    labelPlural: 'Empresas',
    title: 'Gestão de Empresas',
    hasCompanySelect: false,
    isSelectionOption: false,
    hasSalary: false,
    customFields: {
      razao_social: 'Razão Social',
      cnpj: 'CNPJ',
      cidade: 'Cidade',
      estado: 'Estado',
    },
  },
  cadastros_filiais: {
    endpoint: '/branches',
    label: 'Filial',
    labelPlural: 'Filiais',
    title: 'Filiais',
    hasCompanySelect: true,
    isSelectionOption: false,
    hasSalary: false,
  },
  cadastros_cargos: {
    endpoint: '/position-salaries',
    label: 'Cargo',
    labelPlural: 'Cargos',
    title: 'Cargos e Salários',
    hasCompanySelect: false,
    isSelectionOption: false,
    hasSalary: true,
  },
  cadastros_departamentos: {
    endpoint: '/departments',
    label: 'Departamento',
    labelPlural: 'Departamentos',
    title: 'Departamentos',
    hasCompanySelect: false,
    isSelectionOption: false,
    hasSalary: false,
  },
  cadastros_situacoes: {
    endpoint: '/status',
    label: 'Situação',
    labelPlural: 'Situações',
    title: 'Situações',
    hasCompanySelect: false,
    isSelectionOption: false,
    hasSalary: false,
  },
  cadastros_odonto: {
    endpoint: '/odonto-options',
    label: 'Opção de Odonto',
    labelPlural: 'Opções de Odonto',
    title: 'Opções de Odonto',
    hasCompanySelect: false,
    isSelectionOption: true,
    hasSalary: false,
  },
  'cadastros_seg_vida': {
    endpoint: '/seg-vida-options',
    label: 'Opção de Seg. Vida',
    labelPlural: 'Opções de Seguro de Vida',
    title: 'Opções de Seguro de Vida',
    hasCompanySelect: false,
    isSelectionOption: true,
    hasSalary: false,
  },
  cadastros_total_pass: {
    endpoint: '/total-pass-options',
    label: 'Opção de Total Pass',
    labelPlural: 'Opções de Total Pass',
    title: 'Opções de Total Pass',
    hasCompanySelect: false,
    isSelectionOption: true,
    hasSalary: false,
  },
  cadastros_med_ocupacional: {
    endpoint: '/med-ocupacional-options',
    label: 'Opção de Med. Ocupacional',
    labelPlural: 'Opções de Med. Ocupacional',
    title: 'Opções de Med. Ocupacional',
    hasCompanySelect: false,
    isSelectionOption: true,
    hasSalary: false,
  },
  cadastros_enderecos: {
    endpoint: '/company-addresses',
    label: 'Endereço',
    labelPlural: 'Endereços',
    title: 'Endereços da Empresa',
    hasCompanySelect: true,
    isSelectionOption: false,
    hasSalary: false,

    hasAddressFields: true,
  },
  cadastros_transporte: {
    endpoint: '/transporte',
    label: 'Transporte',
    labelPlural: 'Transportes',
    title: 'Transportes',
    hasCompanySelect: false,
    isSelectionOption: true,
    hasSalary: false,
  },
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const getDefaultDialogData = (type) => {
  if (type === 'enderecos') {
    return {
      nome_empresa: '', endereco_completo: '', cep: '', logradouro: '',
      numero: '', complemento: '', bairro: '', cidade: '', estado: '', is_default: false,
    };
  }
  return { name: '', company_id: '', value: '' };
};

const getDefaultCargoData = () => ({
  empresa: '', cargo: '', departamento: '', modalidade: 'CLT',
  ...SALARY_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {}),
});

const CrudSection = ({ tabId }) => {
  const config = TAB_CONFIG[tabId];
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [dialog, setDialog] = useState({ open: false, action: 'create', data: getDefaultDialogData(tabId) });
  const [cargoDialog, setCargoDialog] = useState({ open: false, action: 'create', data: getDefaultCargoData() });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}${config.endpoint}`);
      setItems(res.data);
      if (config.hasCompanySelect || tabId === 'cargos') {
        const compRes = await axios.get(`${API}/companies`);
        setCompanies(compRes.data);
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      toast.error(`Erro ao carregar ${config.labelPlural}`);
    } finally {
      setLoading(false);
    }
  }, [tabId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-fill address when a company is selected (create mode)
  useEffect(() => {
    if (tabId !== 'enderecos' || dialog.action !== 'create' || !dialog.data.nome_empresa) return;
    const existing = items.find(i => i.nome_empresa === dialog.data.nome_empresa);
    if (existing) {
      setDialog(prev => ({
        ...prev,
        data: {
          ...prev.data,
          cep: existing.cep || '',
          logradouro: existing.logradouro || '',
          numero: existing.numero || '',
          complemento: existing.complemento || '',
          bairro: existing.bairro || '',
          cidade: existing.cidade || '',
          estado: existing.estado || '',
          is_default: existing.is_default || false,
        },
      }));
    } else {
      setDialog(prev => ({
        ...prev,
        data: {
          ...prev.data,
          endereco_completo: '', cep: '', logradouro: '', numero: '',
          complemento: '', bairro: '', cidade: '', estado: '', is_default: false,
        },
      }));
    }
  }, [dialog.data.nome_empresa, tabId, dialog.action]);

  // Auto-generate endereco_completo from individual fields
  useEffect(() => {
    if (tabId !== 'enderecos') return;
    const d = dialog.data;
    const parts = [
      d.logradouro,
      d.numero ? `, ${d.numero}` : '',
      d.complemento ? ` - ${d.complemento}` : '',
      d.bairro ? `, ${d.bairro}` : '',
      d.cidade ? ` - ${d.cidade}` : '',
      d.estado ? `/${d.estado}` : '',
      d.cep ? `, CEP: ${d.cep}` : '',
    ].filter(Boolean).join('');
    setDialog(prev => {
      if (prev.data.endereco_completo !== parts) {
        return { ...prev, data: { ...prev.data, endereco_completo: parts } };
      }
      return prev;
    });
  }, [
    dialog.data.logradouro, dialog.data.numero, dialog.data.complemento,
    dialog.data.bairro, dialog.data.cidade, dialog.data.estado, dialog.data.cep,
    tabId,
  ]);

  const handleSaveCargo = async () => {
    const d = cargoDialog.data;
    if (!d.empresa || !d.cargo) { toast.error('Preencha empresa e cargo'); return; }
    try {
      const payload = {
        empresa: d.empresa, cargo: d.cargo, departamento: d.departamento,
        modalidade: d.modalidade,
        ...SALARY_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: d[f.key] ?? 0 }), {}),
      };
      if (cargoDialog.action === 'edit') {
        await axios.put(`${API}/position-salaries/${cargoDialog.data.id}`, payload);
        toast.success('Cargo atualizado!');
      } else {
        await axios.post(`${API}/position-salaries`, payload);
        toast.success('Cargo criado!');
      }
      setCargoDialog({ open: false, action: 'create', data: getDefaultCargoData() });
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar cargo');
    }
  };

  const handleSave = async () => {
    const d = dialog.data;
    if (config.isSelectionOption) {
      if (!d.name) { toast.error('Nome é obrigatório'); return; }
      const payload = { name: d.name, value: d.value ?? 0 };
      try {
        if (dialog.action === 'edit') {
          await axios.put(`${API}${config.endpoint}/${d.id}`, payload);
        } else {
          await axios.post(`${API}${config.endpoint}`, payload);
        }
        toast.success(`${config.label} salvo!`);
        setDialog({ open: false, action: 'create', data: getDefaultDialogData(tabId) });
        fetchData();
      } catch (err) {
        toast.error('Erro ao salvar');
      }
      return;
    }
    if (config.hasAddressFields) {
      if (!d.nome_empresa) { toast.error('Selecione a empresa'); return; }
      if (!d.cep || !d.logradouro || !d.numero || !d.bairro || !d.cidade || !d.estado) {
        toast.error('Preencha todos os campos obrigatórios do endereço'); return;
      }
      const parts = [
        d.logradouro,
        d.numero ? `, ${d.numero}` : '',
        d.complemento ? ` - ${d.complemento}` : '',
        d.bairro ? `, ${d.bairro}` : '',
        d.cidade ? ` - ${d.cidade}` : '',
        d.estado ? `/${d.estado}` : '',
        d.cep ? `, CEP: ${d.cep}` : '',
      ].filter(Boolean).join('');
      const payload = {
        nome_empresa: d.nome_empresa, endereco_completo: parts || d.endereco_completo,
        cep: d.cep, logradouro: d.logradouro, numero: d.numero,
        complemento: d.complemento, bairro: d.bairro, cidade: d.cidade,
        estado: d.estado, is_default: d.is_default,
      };
      try {
        if (dialog.action === 'edit') {
          await axios.put(`${API}${config.endpoint}/${d.id}`, payload);
        } else {
          await axios.post(`${API}${config.endpoint}`, payload);
        }
        toast.success('Endereço salvo!');
        setDialog({ open: false, action: 'create', data: getDefaultDialogData(tabId) });
        fetchData();
      } catch (err) {
        toast.error('Erro ao salvar endereço');
      }
      return;
    }
    if (config.hasCompanySelect) {
      if (!d.name || !d.company_id) { toast.error('Preencha nome e empresa'); return; }
    } else {
      if (!d.name) { toast.error('Nome é obrigatório'); return; }
    }
    const payload = config.hasCompanySelect
      ? { name: d.name, company_id: d.company_id }
      : { name: d.name };
    try {
      if (dialog.action === 'edit') {
        await axios.put(`${API}${config.endpoint}/${d.id}`, payload);
      } else {
        await axios.post(`${API}${config.endpoint}`, payload);
      }
      toast.success(`${config.label} salvo!`);
      setDialog({ open: false, action: 'create', data: getDefaultDialogData(tabId) });
      fetchData();
    } catch (err) {
      toast.error('Erro ao salvar');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    try {
      await axios.delete(`${API}${config.endpoint}/${deleteDialog.item.id}`);
      toast.success(`${config.label} removido!`);
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      toast.error('Erro ao remover');
    }
  };

  const getFilteredData = () => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    if (tabId === 'enderecos') {
      return items.filter((i) =>
        (i.nome_empresa || '').toLowerCase().includes(term) ||
        (i.endereco_completo || '').toLowerCase().includes(term) ||
        (i.cidade || '').toLowerCase().includes(term) ||
        (i.estado || '').toLowerCase().includes(term)
      );
    }
    if (tabId === 'cargos') {
      return items.filter((i) =>
        (i.cargo || '').toLowerCase().includes(term) ||
        (i.empresa || '').toLowerCase().includes(term) ||
        (i.departamento || '').toLowerCase().includes(term)
      );
    }
    return items.filter((i) => (i.name || '').toLowerCase().includes(term) ||
      (config.hasCompanySelect && companies.find((c) => c.id === i.company_id)?.name?.toLowerCase().includes(term)));
  };

  const openCreateDialog = () => {
    setDialog({ open: true, action: 'create', data: getDefaultDialogData(tabId) });
  };

  const openEditDialog = (item) => {
    setDialog({ open: true, action: 'edit', data: { ...item } });
  };

  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      if (response.data.erro) { toast.error('CEP não encontrado'); return; }
      setDialog((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          logradouro: response.data.logradouro || '',
          bairro: response.data.bairro || '',
          cidade: response.data.localidade || '',
          estado: response.data.uf || '',
        },
      }));
    } catch { toast.error('Erro ao buscar CEP'); }
  };

  const filteredData = getFilteredData();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            <span>{config.title}</span>
          </CardTitle>
          <CardDescription>Adicione, edite ou remova {config.labelPlural.toLowerCase()}</CardDescription>
        </div>
        {!config.hasSalary && (
          <Button onClick={openCreateDialog} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" /> Adicionar
          </Button>
        )}
        {config.hasSalary && (
          <Button onClick={() => setCargoDialog({ open: true, action: 'create', data: getDefaultCargoData() })} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" /> Adicionar
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
          <Input
            placeholder={`Buscar ${config.labelPlural.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {tabId === 'enderecos' ? (
                  <>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Endereço</TableHead>
                    <TableHead>Cidade</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-center">Padrão</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </>
                ) : tabId === 'cargos' ? (
                  <>
                    <TableHead>Cargo</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Departamento</TableHead>
                    <TableHead>Modalidade</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </>
                ) : (
                  <>
                    <TableHead>Nome</TableHead>
                    {config.hasCompanySelect && <TableHead>Empresa</TableHead>}
                    {config.customFields && Object.keys(config.customFields).map((f) => (
                      <TableHead key={f}>{config.customFields[f]}</TableHead>
                    ))}
                    {config.isSelectionOption && <TableHead className="text-right">Valor</TableHead>}
                    <TableHead className="text-center">Ações</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8">Carregando...</TableCell></TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                    {searchTerm ? 'Nenhum item encontrado' : 'Nenhum item cadastrado'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((item) => (
                  <TableRow key={item.id}>
                    {tabId === 'enderecos' ? (
                      <>
                        <TableCell className="font-medium">{item.nome_empresa}</TableCell>
                        <TableCell>{item.endereco_completo}</TableCell>
                        <TableCell>{item.cidade}</TableCell>
                        <TableCell>{item.estado}</TableCell>
                        <TableCell className="text-center">
                          {item.is_default ? <Badge>Padrão</Badge> : <Badge variant="outline">-</Badge>}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)}
                              className="text-blue-600"><Edit2 className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, item })}
                              className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </>
                    ) : tabId === 'cargos' ? (
                      <>
                        <TableCell className="font-medium">{item.cargo}</TableCell>
                        <TableCell>{item.empresa}</TableCell>
                        <TableCell>{item.departamento || '-'}</TableCell>
                        <TableCell>{item.modalidade || '-'}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-2">
                            <Button variant="ghost" size="sm"
                              onClick={() => setCargoDialog({ open: true, action: 'edit', data: { ...item } })}
                              className="text-blue-600"><Edit2 className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm"
                              onClick={() => setDeleteDialog({ open: true, item })}
                              className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        {config.hasCompanySelect && (
                          <TableCell>{companies.find((c) => c.id === item.company_id)?.name || '-'}</TableCell>
                        )}
                        {config.customFields && Object.keys(config.customFields).map((f) => (
                          <TableCell key={f}>{item[f] || '-'}</TableCell>
                        ))}
                        {config.isSelectionOption && (
                          <TableCell className="text-right font-medium text-green-600">
                            {formatCurrency(item.value)}
                          </TableCell>
                        )}
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)}
                              className="text-blue-600"><Edit2 className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, item })}
                              className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Generic Create/Edit Dialog */}
      {!config.hasSalary && (
        <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, action: 'create', data: getDefaultDialogData(tabId) })}>
          <DialogContent className={config.hasAddressFields ? 'max-w-2xl' : ''}>
            <DialogHeader>
              <DialogTitle>{dialog.action === 'edit' ? 'Editar' : 'Adicionar'} {config.label}</DialogTitle>
              <DialogDescription>Preencha as informações abaixo</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {config.hasAddressFields ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Empresa <span className="text-red-500">*</span></Label>
                      <Select value={dialog.data.nome_empresa} onValueChange={(v) => setDialog((p) => ({ ...p, data: { ...p.data, nome_empresa: v } }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>CEP <span className="text-red-500">*</span></Label>
                      <div className="flex gap-2">
                        <Input value={dialog.data.cep} onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, cep: e.target.value } }))} placeholder="00000-000" />
                        <Button variant="outline" size="icon" onClick={() => buscarCEP(dialog.data.cep)}><Search className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Endereço Completo</Label>
                    <Input value={dialog.data.endereco_completo} readOnly className="bg-slate-50" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Logradouro <span className="text-red-500">*</span></Label><Input value={dialog.data.logradouro} onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, logradouro: e.target.value } }))} /></div>
                    <div><Label>Número <span className="text-red-500">*</span></Label><Input value={dialog.data.numero} onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, numero: e.target.value } }))} inputMode="numeric" /></div>
                    <div><Label>Complemento</Label><Input value={dialog.data.complemento} onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, complemento: e.target.value } }))} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Bairro <span className="text-red-500">*</span></Label><Input value={dialog.data.bairro} onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, bairro: e.target.value } }))} /></div>
                    <div><Label>Cidade <span className="text-red-500">*</span></Label><Input value={dialog.data.cidade} onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, cidade: e.target.value } }))} /></div>
                    <div><Label>Estado <span className="text-red-500">*</span></Label><Input value={dialog.data.estado} onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, estado: e.target.value.toUpperCase().slice(0, 2) } }))} maxLength={2} /></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="is_default" checked={dialog.data.is_default}
                      onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, is_default: e.target.checked } }))}
                      className="rounded border-slate-300" />
                    <Label htmlFor="is_default">Endereço padrão</Label>
                  </div>
                </>
              ) : config.isSelectionOption ? (
                <>
                  <div><Label>Nome</Label><Input value={dialog.data.name} onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, name: e.target.value } }))} /></div>
                  <div><Label>Valor</Label><CurrencyInput value={dialog.data.value} onChange={(v) => setDialog((p) => ({ ...p, data: { ...p.data, value: v } }))} /></div>
                </>
              ) : (
                <>
                  <div><Label>Nome</Label><Input value={dialog.data.name} onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, name: e.target.value } }))} /></div>
                  {config.hasCompanySelect && (
                    <div>
                      <Label>Empresa</Label>
                      <select value={dialog.data.company_id} onChange={(e) => setDialog((p) => ({ ...p, data: { ...p.data, company_id: e.target.value } }))}
                        className="w-full px-3 py-2 border rounded-md">
                        <option value="">Selecione</option>
                        {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialog({ open: false, action: 'create', data: getDefaultDialogData(tabId) })}>Cancelar</Button>
              <Button onClick={handleSave} className="btn-primary">{dialog.action === 'edit' ? 'Atualizar' : 'Salvar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Cargo + Salary Dialog */}
      {config.hasSalary && (
        <Dialog open={cargoDialog.open} onOpenChange={(o) => !o && setCargoDialog({ open: false, action: 'create', data: getDefaultCargoData() })}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{cargoDialog.action === 'edit' ? 'Editar' : 'Adicionar'} Cargo</DialogTitle>
              <DialogDescription>Preencha os dados do cargo e suas faixas salariais</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Empresa *</Label>
                  <select value={cargoDialog.data.empresa} onChange={(e) => setCargoDialog((p) => ({ ...p, data: { ...p.data, empresa: e.target.value } }))}
                    className="w-full px-3 py-2 border rounded-md">
                    <option value="">Selecione</option>
                    {companies.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Cargo *</Label>
                  <Input value={cargoDialog.data.cargo} onChange={(e) => setCargoDialog((p) => ({ ...p, data: { ...p.data, cargo: e.target.value } }))} />
                </div>
                <div>
                  <Label>Departamento</Label>
                  <Input value={cargoDialog.data.departamento} onChange={(e) => setCargoDialog((p) => ({ ...p, data: { ...p.data, departamento: e.target.value } }))} />
                </div>
                <div>
                  <Label>Modalidade</Label>
                  <select value={cargoDialog.data.modalidade} onChange={(e) => setCargoDialog((p) => ({ ...p, data: { ...p.data, modalidade: e.target.value } }))}
                    className="w-full px-3 py-2 border rounded-md">
                    <option value="CLT">CLT</option>
                    <option value="PJ">PJ</option>
                  </select>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-medium">Faixas Salariais</h4>
                <div className="grid grid-cols-3 gap-4">
                  {SALARY_FIELDS.map((f) => (
                    <div key={f.key}>
                      <Label>{f.label}</Label>
                      <CurrencyInput value={cargoDialog.data[f.key]} onChange={(v) => setCargoDialog((p) => ({ ...p, data: { ...p.data, [f.key]: v } }))} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCargoDialog({ open: false, action: 'create', data: getDefaultCargoData() })}>Cancelar</Button>
              <Button onClick={handleSaveCargo} className="btn-primary" disabled={!cargoDialog.data.empresa || !cargoDialog.data.cargo}>
                {cargoDialog.action === 'edit' ? 'Atualizar' : 'Salvar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => setDeleteDialog({ open: o, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover <strong>{deleteDialog.item?.name || deleteDialog.item?.cargo}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete}>Remover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CrudSection;
